/**
 * The educational layer: every statistic on the site, explained where it is
 * read.
 *
 * Plan §4 makes an "educational sidebar" a v1 commitment and §14.4 keeps it on
 * the launch list. This is that commitment, built as one mechanism rather than
 * as prose sprinkled through four render modules — because the explanations are
 * **content**, they will be reviewed and rewritten as a set, and copy that lives
 * beside the markup that displays it never gets read as a set by anybody.
 *
 * So `EXPLANATIONS` below is the whole body of writing, in one table, in
 * reading order. Everything else in this file is plumbing.
 *
 * ## What an explanation owes the reader
 *
 * Two things, and the second is the point: **what the number is**, and **why it
 * matters**. A definition alone is a restatement of the label — anyone who
 * needed "σ" expanded to "standard deviation" is no better off. What a reader
 * cannot get from the screen is the consequence: that one extreme weight
 * decides how many usable levels the other million get, that a finetune can
 * move 2% of a tensor and leave the rest alone, that a cosine of 1.0 beside a
 * large relF means somebody multiplied the tensor and changed nothing about
 * what it points at.
 *
 * The register these are written in is the one the rest of the site earns trust
 * with — `analysis/compare.ts`'s doc comments, the sampling caveats, the
 * shared-binning note. Concrete, exact about limits, no adjectives doing work a
 * fact should do. Where a statistic is genuinely dull, it is one short sentence;
 * a flourish on a boring number reads worse than a flat line.
 *
 * **Pairs are named as pairs.** `compare.ts` is explicit that some of these
 * statistics are meaningless alone — relF without delta participation, cosine
 * without the two σ — and where that is true the explanation says so and names
 * the companion, because a reader who takes one away without the other has been
 * handed the misleading half.
 *
 * ## Accessibility, and the tab order
 *
 * A tooltip that only answers to hover is unreachable by keyboard and by touch,
 * so these are real `<button>`s and the bubble satisfies WCAG 1.4.13 (dismiss
 * with Escape without moving focus, hoverable, persistent until dismissed).
 *
 * They are **not** individual tab stops. The accessibility pass that took this
 * page from 249 tab stops to 15 established the rule: a homogeneous set of many
 * similar controls is one tab stop plus arrow keys — the tensor list did it
 * (160 rows → 1), the anatomy grid did it (76 cells → 1). Roughly twenty
 * explain icons taken individually would put a supplementary control between
 * every statistic and the next real one, and more than double the page's tab
 * stops to reach content that is never the primary path to the number. So the
 * whole set is one stop, arrow keys move within it, and the bubble is wired
 * with `aria-describedby` so a screen reader announces the explanation on focus
 * without anything having to be opened at all.
 */

import { setRovingTabindex } from "./roving-focus.js";

/**
 * What this particular cell's scheme costs, worked out from the cell.
 *
 * ## Why this is computed and not written
 *
 * The scheme label is `bits@group` and both numbers vary — `4@64`, `8@64`,
 * `2@128` all appear in one MLX repo, sometimes in one tower. A sentence with a
 * worked example in it is right for exactly one of them and wrong under every
 * other heading, which is what shipped: an `8@64` cell measured at 8.50 carried
 * a body explaining how `4@64` reaches 4.50.
 *
 * ## Why the total is measured rather than derived
 *
 * The obvious fix is to compute `bits + 2 * 16 / group`, which is correct for
 * MLX — it stores a BF16 scale and a BF16 offset per group. It is **not**
 * correct in general: `bits@group` labels are produced for GPTQ and the other
 * packed-integer schemes too, and their zero points are packed at the weight
 * width rather than stored as 16-bit floats. Deriving the total from MLX's
 * layout would print a confident wrong number for those.
 *
 * So the total is `effectiveBpw`, which `anatomy.ts` measured from the actual
 * byte ranges, and the overhead is the difference. That holds for any scheme
 * whatever its sidecars are, and it is the same figure the cell prints, so the
 * sentence cannot disagree with the number beside it.
 *
 * Returns `undefined` when the label does not parse or the measurement does not
 * exceed the weight width — there is no story to tell then, and inventing one is
 * how the last two errors in this file happened.
 */
export function schemeCostSentence(
  label: string,
  effectiveBpw: number,
): string | undefined {
  const parsed = /^(\d+(?:\.\d+)?)@(\d+)$/.exec(label.trim());
  if (!parsed) return undefined;
  const bits = Number(parsed[1]);
  const group = Number(parsed[2]);
  if (!(bits > 0) || !(group > 0)) return undefined;
  if (!Number.isFinite(effectiveBpw) || effectiveBpw <= bits) return undefined;
  const overhead = effectiveBpw - bits;
  return `This cell is <strong>${parsed[1]}-bit</strong> weights in groups of ${group}, and
    costs <strong>${effectiveBpw.toFixed(2)}</strong> bits per weight — the extra
    ${overhead.toFixed(2)} is the scale and offset, spread across each group.`;
}

/** One entry in the glossary: the term as written, and the explanation. */
export interface Explanation {
  /** Short display name — also what the icon's accessible name is built from. */
  readonly term: string;
  /** Body copy. Authored HTML: `<code>`, `<strong>`, `<em>` only. */
  readonly body: string;
  /**
   * Accessible name for the icon, when `What <term> means` does not read.
   *
   * That template works for the nouns almost every entry has — "What shape
   * means", "What kurtosis means" — and falls apart for a term that is already
   * a phrase: "What How TensorLens treats the Hub means" is a sentence nobody
   * can parse, and it is read aloud to exactly the people who cannot see the
   * heading it duplicates.
   */
  readonly label?: string;
}

/**
 * Every explanation on the site, in one place, in roughly the order a visitor
 * meets them: what a tensor is made of, what its weights look like, what
 * quantization costs, and what changed between two repos.
 */
export const EXPLANATIONS = {
  /* -- the footer's one explanation ---------------------------------------- */

  etiquette: {
    // Capitalised in the data rather than by CSS. A `::first-letter` rule would
    // catch every term, and two of them must not be touched: `relF` and `p₀`
    // carry meaning in their casing.
    term: "How TensorLens treats the Hub",
    label: "How TensorLens treats the Hub",
    body: `This tool is a reader. It never downloads a model, never walks a repo looking
      for more, and never fetches a tensor you did not click — a whole inventory is a
      couple of small reads of the file's index, and opening one tensor fetches only that
      tensor's bytes. That restraint is the deal: Hugging Face serves these files to
      everyone for free, and a viewer that hammered them would deserve to be blocked.
      <strong>Everything runs in this browser tab and talks only to Hugging Face</strong>
      — there is nowhere else for it to send anything. Public repos only.
      <em>What a model is made of</em> is also kept in this browser after the
      first look, so returning to a model you have already opened costs the Hub
      nothing at all. Only the index is kept, never the weights, and it is keyed
      to an exact commit so it can never show you a stale one. Best effort: if
      the browser will not store it, everything simply works as it did before.`,
  },

  /* -- what a tensor is ---------------------------------------------------- */

  shape: {
    term: "shape",
    body: `The tensor's dimensions. A <code>4096 × 11008</code> weight matrix is 4096 rows
      of 11008 numbers each. Shape is also what decides whether two tensors can be compared
      at all: a matching name with a different shape is a lineage mismatch, not two versions
      of one weight, so it is reported rather than truncated to fit.`,
  },

  towers: {
    term: "towers",
    body: `A tower is one self-contained stack of layers. A multimodal checkpoint holds
      several — a text tower, a vision tower that turns images into something the text tower
      can read, sometimes an audio one — plus whatever they share, like the output head.
      One at a time is the only honest view: layer 7 of the vision tower and layer 7 of the
      text tower are unrelated pieces of machinery that happen to carry the same index, so a
      grid of "all towers" would stack two different models on top of each other and invite
      you to compare rows that mean nothing to one another.
      Towers are also the unit people quantize separately. A quantizer can be told to skip
      one and often is — a vision tower left at 16 bits beside a text tower taken down to 4
      is a common shape — which is one reason the figures above change as you switch. Whole
      towers get dropped as well: that is how a text-only build of a model that could once
      see is made.`,
  },

  dtype: {
    term: "dtype",
    body: `How each number is written in the file. <code>F32</code> is a 32-bit float;
      <code>BF16</code> is 16 bits with the same exponent range and fewer significant digits;
      <code>U8</code> is a single unsigned byte. It fixes the file's size and the precision
      the file can hold — but it is <em>not</em> the model's precision. A quantized repo
      stores packed integers in a <code>U8</code> or <code>U32</code> tensor and rebuilds the
      weights from separate scale and bias planes, so <code>U8</code> there means "packed",
      not "8-bit weights".
      A GGUF file named <code>Q4_K_M</code> is not stored in a type called
      <code>Q4_K_M</code>. That is a <strong>recipe</strong>: it stores most tensors as
      <code>Q4_K</code> and a few the recipe treats as more sensitive at a higher rate, such
      as <code>Q6_K</code>. The types named on this page are the per-tensor ones, so a file
      named for one of them holds several.`,
  },

  "quant-scheme": {
    term: "quantization scheme",
    label: "What the bits-at-group-size label means",
    /*
     * No worked example in here, deliberately.
     *
     * It used to say "4@64 lands at 4.50", which is true and was printed under
     * the heading of whatever cell you hovered — including an `8@64` cell
     * reading 8.50, where the body explained a different scheme than the one it
     * was attached to (owner, 2026-08-19). A fixed example cannot serve a label
     * that varies, so the arithmetic moved to `schemeCostSentence`, which reads
     * it off the cell.
     *
     * A second example outlived that fix by one commit — "which is why a 4-bit
     * scheme reads as `U32`", printed under an 8-bit heading. Removed with the
     * scheme-versus-dtype sentence it sat in, which the panel did not need: the
     * `U32` note directly below it makes the same point about the cell actually
     * on screen, and the glossary's own `dtype` entry makes it there. Two
     * statements of one idea, and the general one was the one that could be
     * wrong about the specific case.
     */
    body: `The two numbers are the weight width and the group size: how many bits each
      weight gets, and how many weights share one scale — and, in most schemes, an offset.`,
  },

  params: {
    term: "parameters",
    // Broken down by dtype, this counts *stored* elements, and the app shows it
    // that way — but the entry may not say "in this table". The glossary renders
    // these bodies standalone in an A-to-Z, where a reference to a table the
    // reader cannot see is the same failure as the model-specific example this
    // entry's neighbour had to lose (owner, 2026-08-10).
    body: `How many individual numbers a tensor holds — the product of its dimensions. Counted
      per dtype that is the elements each dtype physically stores, and two things can make it
      larger than the model's parameter count. A packed payload holds more than one weight per
      element: one <code>I32</code> can carry ten three-bit weights, so such a row counts words
      rather than weights and its bits-per-weight reads as the storage width. And some stored
      tensors are not weights at all — a causal mask, rotary frequencies, a table of position
      ids are constants a model ships rather than values it learned, so they occupy elements and
      bytes while adding nothing to what the model knows. A model's own parameter count has the
      packing undone and the constants left out.`,
  },

  bytes: {
    term: "bytes",
    body: `File space, in binary units — 1 KiB is 1024 B, because a byte offset in a file is
      literally what this is. Model cards usually quote decimal GB, about 7% larger at that
      scale, which is enough to make two correct numbers look like they disagree.
      Multiply by 8 for bits, divide by parameters, and you have the real cost per weight.`,
  },

  "tensor-count": {
    term: "tensors",
    body: `How many separate arrays the repo stores. It is not a measure of size, and it can
      move the wrong way: a quantized repo has <em>more</em> tensors than the model it came
      from, because every packed weight travels with its own scale and bias planes. Those
      triples are folded back into one logical tensor before anything here is counted or
      diffed — otherwise a 4-bit version of a 24-layer model reads as hundreds of unrelated
      additions.`,
  },

  "byte-share": {
    term: "share of bytes",
    body: `What fraction of a repo's weight bytes one dtype accounts for. It is how a
      "4-bit" repo admits how much of itself is not 4-bit: embeddings, norms and the output
      head are routinely left at full precision, and on a small model the embedding alone can
      be a quarter of the file.`,
  },

  "dtype-bpw": {
    term: "bits per weight",
    body: `This dtype's bytes divided by its parameters, times eight — measured from the
      file, not read off the format's name. For a block format the two differ, and the gap is
      the point: <code>Q4_K</code> is four-bit quants but lands near 4.5, because each
      256-element super-block also carries a scale, a min, and their own quantized
      per-group scales. <code>IQ2_XXS</code> lands near 2.06 for the same reason in the other
      direction. Read down the column and the model's real cost is the byte-weighted average
      of it, not the number in the filename — which names a target size, not a format.`,
  },

  /* -- what the weights look like ------------------------------------------ */

  sigma: {
    term: "σ (RMS)",
    body: `The typical size of a value in the tensor: the square root of the mean of the squares, taken
      about <em>zero</em> rather than about the mean. Weight tensors sit almost exactly on zero
      by construction, and every quantization scale is derived in the about-zero convention, so
      this σ is directly comparable to the scale a quantizer would pick. Read it beside
      <strong>mean |w|</strong>: a bell curve gives mean |w| ≈ 0.8σ, and a much smaller ratio
      means the total is being carried by a few large values rather than by the bulk.`,
  },

  mean: {
    term: "mean",
    body: `The plain average, sign kept. Near zero is the expected reading for weights, and for
      the delta between two versions of a tensor it is the more interesting one — a clearly
      non-zero mean is a systematic push in one direction, which an about-zero σ cannot see.`,
  },

  "mean-abs": {
    term: "mean |w|",
    body: `The average distance of a weight from zero, ignoring sign. On its own it is
      unremarkable; against σ it is a free tail detector. A Gaussian gives mean |w| ≈ 0.8σ.
      Much below that and most weights sit near zero while a handful of large ones own the
      total — the condition under which one scale cannot serve both groups.`,
  },

  "max-abs": {
    term: "max |w|",
    body: `The single largest magnitude present. It matters out of all proportion to being one
      number: any per-tensor scale has to stretch far enough to represent it, so one extreme
      value decides how many usable levels the other million get. That is the mechanism behind
      most quantization damage. When a tensor is too large to read whole and only some of its
      rows were fetched, this is a <strong>floor, not a maximum</strong> — the largest value in a
      sample can only understate the largest value in the tensor.`,
  },

  kurtosis: {
    term: "kurtosis",
    body: `How heavy the tails are: <code>E[w⁴] ÷ E[w²]²</code>, about zero. A normal
      distribution sits at 3. Higher means the same total is concentrated in fewer, larger
      values — a compact way of saying "this tensor has outliers", and the exact condition
      under which min/max ranging and a single per-tensor scale fail.`,
  },

  sparsity: {
    term: "sparsity",
    body: `Share of values that are exactly <code>0.0</code> — exact comparison, not "close to
      zero". A densely trained tensor is usually near 0%. A large number here means something
      deliberately zeroed weights: pruning, a structured mask, or a quantization that collapsed
      a whole range onto zero. Which of the three it was, the weight distribution will tell you.`,
  },

  "row-spread": {
    term: "row norm p99 ÷ p50",
    body: `The 99th-percentile row length divided by the median row length. In an evenly
      balanced matrix this is near 1. In real transformer weights it is routinely 8, often 50,
      sometimes far more — a handful of rows thousands of times larger than their neighbours.
      <strong>This is why naive quantization destroys models.</strong> A scale wide enough for
      the hot rows rounds every median row to zero; a scale that fits the median clips the hot
      ones. There is no single number that serves both, which is why finer granularity —
      per-group or per-row scales — is the first fix, before any argument about conventions.`,
  },

  "col-spread": {
    term: "column norm p99 ÷ p50",
    body: `<strong>Row norm p99 ÷ p50</strong> read down the other axis, and worth having both
      because a scale convention has to choose one. It is omitted entirely when only part of a
      large tensor was read, rather than estimated: a column norm needs every row, so from a
      sample it would be a confident-looking guess.`,
  },

  "non-finite": {
    term: "non-finite values",
    body: `Elements that were <code>NaN</code> or <code>±Infinity</code>. They are excluded from
      every statistic here and <em>counted</em> rather than quietly dropped, because the count is
      the finding: a released checkpoint carrying non-finite weights has something wrong upstream
      — a diverged run, a bad conversion — and no statistic computed over the rest of the tensor
      would ever mention it.`,
  },

  /* -- what quantization costs --------------------------------------------- */

  "effective-bpw": {
    term: "effective bits per weight",
    body: `Bytes × 8 is bits; divide those by parameters and you have what one weight
      actually costs, all overhead included.
      <strong>A "4-bit" model is never 4 bits.</strong> Every group of weights also stores a
      scale and a bias, each a 16-bit number. Spread across a group of 32 that is half a bit
      per weight apiece, so 4-bit at group 32 costs 5.00 — and at the group 64 most repos
      actually use, 4.50. This is the number to compare across repos, because it is the one
      whose definition nobody gets to choose.`,
  },

  "ternary-scale": {
    // "ternary scale" and not the bare "scale" the column header uses. In the
    // app the heading sits directly above a ternary comparison table, so the
    // context is free; in the glossary's A-to-Z it would be an unqualified word
    // that could mean any of four things this tool talks about.
    term: "ternary scale",
    body: `The single multiplier a ternary code uses: every weight becomes <code>−s</code>,
      <code>0</code> or <code>+s</code>. All three conventions produce those same three levels
      and differ only in how they pick <code>s</code> — so the choice of scale, not the code,
      is where the error comes from.`,
  },

  "zero-fraction": {
    term: "p₀",
    body: `The share of weights a ternary code sends to exactly zero. Always read it beside
      relF: relF is norm-relative, so a scale that reproduces one big outlier faithfully can
      score a <em>better</em> relF than a sensible one while deleting most of the tensor. A p₀
      near 90% next to a flattering relF is that failure, caught in the act.`,
  },

  /* -- what changed between two repos -------------------------------------- */

  relf: {
    term: "relF",
    body: `<strong>Relative Frobenius change</strong>: <code>‖B − A‖ ÷ ‖A‖</code> — the size of
      the change as a fraction of what was there before. (The Frobenius norm is the square root
      of the sum of every element squared: a matrix's version of a vector's length.) One number,
      and that is exactly its limit — it cannot tell a change spread thinly over every weight
      from one concentrated in a handful, because both score the same whenever the totals match.
      It is never shown alone here. Its companion is <strong>delta participation</strong> when
      comparing two repos, and <strong>p₀</strong> in the ternary what-if table; without one of
      those, relF will happily rate a setting that reproduced an outlier and deleted everything
      else as the better one.`,
  },

  participation: {
    term: "delta participation",
    body: `How much of the tensor did the moving, as a fraction of it. 1.0 means every weight
      shifted by about the same amount. ⅓ is what an ordinary Gaussian change gives. 0.02 means
      the movement's mass sits in roughly 2% of the weights and the other 98% barely stirred —
      <strong>and that last case is the common one for a finetune</strong>, which is a genuinely
      odd fact about how finetuning works: a model can be meaningfully changed by moving almost
      none of it. Arithmetically it is <code>(Σd²)² ÷ (n·Σd⁴)</code>, the normalized
      participation ratio borrowed from localization physics, and it is scale-invariant — so it
      is independent of relF rather than a restatement of it. relF says how far; this says how
      much of the tensor went along.`,
  },

  cosine: {
    term: "cosine",
    body: `Which way B points relative to A: <code>⟨A,B⟩ ÷ (‖A‖·‖B‖)</code>. 1.0 is exactly the
      same direction, 0 is unrelated, −1 is opposite. It is scale-invariant on purpose, and that
      is what makes it worth reading beside <strong>σ(A)</strong> and <strong>σ(B)</strong>: a
      cosine of 1.0 next to a large relF means somebody multiplied the tensor and changed nothing
      at all about what it points at. A finetune that only rescaled has learned nothing; a
      quantization round trip that rescaled has a scale-convention bug. Cosine cannot see size,
      by design — the two σ are what see it.`,
  },

  "sigma-ratio": {
    term: "σ(B) ÷ σ(A)",
    body: `How much bigger or smaller B is than A overall; 1.0 means the same size, whatever else
      changed. This is the half of the reading that cosine deliberately cannot supply — together
      they separate "B points somewhere else" from "B is A, doubled".`,
  },

  "changed-fraction": {
    term: "changed weights",
    body: `Share of compared pairs where B differs from A <em>at all</em> — exact inequality, not
      "differs by more than ε". A threshold would need a scale and the right scale differs per
      tensor, so the honest question is the plain one. Expect it well below 100% on real lineage:
      a quantize/dequantize round trip leaves many weights bit-identical, and a merged LoRA leaves
      most of the tensor untouched.`,
  },

  "unchanged-fraction": {
    term: "unchanged weights",
    body: `Share of compared pairs where B is bit-identical to A — the complement of
      <strong>changed weights</strong>, counted rather than subtracted from 1. A high number is
      not evidence that nothing happened: <strong>delta participation</strong> and the delta's
      own distribution are where you find out whether the weights that did move were the ones
      that mattered.`,
  },

  "largest-edit": {
    term: "largest single edit",
    body: `<code>max |B − A|</code>: the biggest change made to any one weight. Beside the delta's
      σ it separates a change built from many small edits from one dominated by a few large ones.
      When only part of a large tensor was read it is a floor rather than a maximum — and for a
      comparison whose subject is often a handful of edited weights, that understatement is the
      one most worth knowing about.`,
  },

  "delta-sigma": {
    term: "σ of the change",
    body: `The typical size of one weight's edit, in the same units as the weights themselves.
      Where relF is relative, this is absolute — which is the reading that survives when ‖A‖ is
      zero and relF cannot be defined at all.`,
  },

  "delta-mean": {
    term: "mean change",
    body: `The average of <code>B − A</code>, sign kept. Near zero means the edits pushed both
      ways and cancelled. A clearly non-zero value is a systematic bias in the change — an added
      offset, or a rescale — and it is invisible to an about-zero σ, which is why it gets its own
      line.`,
  },

  "delta-mean-abs": {
    term: "mean |change|",
    body: `The average size of an edit, ignoring sign. Held against the mean change it says
      whether the edits pushed one way or cancelled out: much larger than |mean change| means the
      movement was two-sided.`,
  },

  /* -- the inventory diff -------------------------------------------------- */

  "diff-status": {
    term: "status",
    body: `Which way a tensor's header differs between the two repos being compared, where
      <strong>A</strong> is the first repo and <strong>B</strong> the second. Always spelled out
      in words and never carried by colour alone — and always about the <em>header</em>: two
      tensors can agree on name, dtype and shape and still hold entirely different numbers.`,
  },

  identical: {
    term: "identical",
    body: `A tensor with the same name, the same dtype and the same shape in both repos. That is
      a statement about headers only: the weights inside can differ completely and this count
      will not notice. Open the tensor to compare the actual numbers.`,
  },

  "dtype-changed": {
    term: "dtype changed",
    body: `A tensor present in both repos, with the same shape, but stored as a different numeric
      type — an F32 checkpoint republished as BF16, say. The values may be identical to within
      the narrower type's precision, or not. This counts the re-encoding, not its effect.`,
  },

  quantized: {
    term: "quantized",
    body: `A tensor present in both repos, where B stores in packed form what A stored as plain
      floats. This is the count that says "B is a compression of A" rather than "B is a different
      model" — and it is the reason a repo pair can lose most of its bytes while keeping every
      parameter.`,
  },

  dequantized: {
    term: "dequantized",
    body: `A tensor present in both repos, where A was packed and B is plain floats — the reverse
      of <strong>quantized</strong>. Rarer, and more interesting when it turns up, because
      unpacking cannot recover what packing discarded: the file grew back and the information
      did not.`,
  },

  "shape-changed": {
    term: "shape changed",
    body: `A tensor with the same name in both repos and different dimensions — a resized
      vocabulary, a different hidden size, a merge that altered the architecture. There is no
      honest way to compare the numbers across a shape change, so those rows are listed but not
      openable.`,
  },

  "only-a": {
    term: "only in A",
    body: `A tensor name present in the first repo and absent from the second — a dropped bias, a
      fused pair of projections, a different naming convention for the same thing. A handful is
      ordinary. Hundreds means these two are not versions of each other.`,
  },

  "only-b": {
    term: "only in B",
    body: `A tensor name the second repo has and the first does not — an added adapter, a newly
      split projection, or the packing metadata a quantization brings with it. As with
      <strong>only in A</strong>, a handful is ordinary and hundreds is a verdict.`,
  },

  comparable: {
    term: "comparable",
    body: `Tensors that both sides reduce to the same logical shape — the only sense in which
      "the same tensor" has two versions, and therefore the only rows you can open. It is the
      count that decides how much of this comparison is actually available, which is why it is
      stated rather than left to be inferred from the others.`,
  },
} as const satisfies Record<string, Explanation>;

export type ExplainKey = keyof typeof EXPLANATIONS;

/**
 * Ids have to be unique across a page, and the same statistic legitimately
 * appears twice — σ(A) and σ(B) share a key and need two bubbles. A counter is
 * enough: nothing here is server-rendered, so the ids need only be unique, not
 * stable.
 */
let sequence = 0;

/**
 * The ring-with-an-i, as an inline SVG.
 *
 * Inline rather than a font glyph or an image so it inherits `currentColor` and
 * therefore re-themes with the page; `focusable="false"` because IE-era SVG
 * focusability still surfaces in some engines and this is emphatically not a
 * second tab stop inside a button that is already one.
 */
const ICON = `<svg class="explain-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
  <circle cx="8" cy="8" r="6.7" fill="none" stroke="currentColor" stroke-width="1.4"/>
  <circle cx="8" cy="4.8" r="0.95" fill="currentColor"/>
  <rect x="7.2" y="6.9" width="1.6" height="4.9" rx="0.8" fill="currentColor"/>
</svg>`;

/**
 * One explain icon and its bubble, as markup.
 *
 * The bubble is emitted **beside the button and always present in the DOM**,
 * not created on demand. That is what makes `aria-describedby` reliable: a
 * description that only exists while a tooltip is open is a description an
 * assistive technology may compute before it exists. Closed, it is clipped to
 * nothing by CSS rather than `display: none`, so it stays in the accessibility
 * tree the whole time; `role="tooltip"` keeps it out of the linear reading
 * order, where twenty of these would be a wall.
 */
export function explainIcon(key: ExplainKey): string {
  // Annotated to the interface rather than inferred. EXPLANATIONS is a literal
  // object, so an inferred lookup is a union of every entry's exact shape, and
  // `label` exists on only one of them — reading it would be an error on all
  // the others. The annotation is also what keeps the optional field optional
  // rather than requiring it everywhere.
  return explainIconFor(EXPLANATIONS[key], key);
}

/**
 * The same icon and bubble for an explanation that is not in `EXPLANATIONS`.
 *
 * The storage-type copy lives in its own table (`dtype-explanations.ts`) and is
 * keyed by type name rather than by an `ExplainKey`: there are forty-two of
 * them, they come from two source enums, and folding them into the site-wide
 * table would bury nineteen hand-written entries under a format list. They are
 * still the same *kind* of thing and get the same plumbing, which is the whole
 * reason this seam exists rather than a second tooltip implementation.
 *
 * `slug` only names the DOM id and the `data-explain-for` hook, so it is
 * restricted to characters that are safe there — a dtype string is
 * network-controlled (the header parser lets unknown ones through on purpose),
 * and it would otherwise reach an `id` attribute unescaped.
 */
export function explainIconFor(entry: Explanation, slug: string): string {
  sequence += 1;
  const safeSlug = slug.replace(/[^A-Za-z0-9_-]/g, "-");
  const id = `explain-${safeSlug}-${sequence}`;
  return `<span class="explain" data-explain-for="${safeSlug}"><button class="explain-toggle"
    type="button" tabindex="-1" aria-expanded="false" aria-describedby="${id}"
    aria-label="${escapeAttr(entry.label ?? `What ${entry.term} means`)}">${ICON}</button><span class="explain-note"
    id="${id}" role="tooltip"><strong class="explain-term">${escapeAttr(entry.term)}</strong>
    ${entry.body}</span></span>`;
}

/**
 * Escape for an attribute value or a text node.
 *
 * `term` reaches both, and for a storage-type entry it is a dtype name. Every
 * name in the table today is alphanumeric, so this changes no output — it is
 * here because the table is keyed by a string that came off the network, and
 * "every current entry is safe" is a property of the data rather than of the
 * code.
 */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * The same glyph, as a link to somewhere the explanation is already written.
 *
 * For `/glossary`, which carries the footer's etiquette icon like every other
 * page but is itself the page that defines the term. A tooltip there would be a
 * popup reproducing text a few hundred pixels further down the same document,
 * and it would need a script on a page whose content deliberately needs none.
 *
 * Same box, same accessible name, same position as `explainIcon` — only the
 * destination differs, which is what `aria-label` was already describing.
 */
export function explainLink(key: ExplainKey, href: string): string {
  const entry: Explanation = EXPLANATIONS[key];
  return `<span class="explain"><a class="explain-toggle explain-link" href="${href}"
    aria-label="${entry.label ?? `What ${entry.term} means`}">${ICON}</a></span>`;
}

/** A label with its explain icon — the shape almost every caller wants. */
export function labelWithExplain(label: string, key: ExplainKey | undefined): string {
  return key === undefined ? label : `${label}${explainIcon(key)}`;
}

/* -- behaviour ------------------------------------------------------------- */

/**
 * How long the bubble survives the pointer leaving, so it can be reached.
 *
 * WCAG 1.4.13 requires hoverable content: the pointer has to be able to travel
 * from the trigger onto the bubble without it vanishing on the way. There is a
 * few pixels of gap between the two, and crossing it leaves the wrapper.
 */
const HOVER_GRACE_MS = 160;

/** Gap between the trigger and the bubble, and the margin kept off any edge. */
const BUBBLE_OFFSET = 6;
const VIEWPORT_MARGIN = 8;

let openWrapper: HTMLElement | undefined;
let closeTimer: ReturnType<typeof setTimeout> | undefined;
let globalListenersAttached = false;

function close(): void {
  if (closeTimer !== undefined) clearTimeout(closeTimer);
  closeTimer = undefined;
  if (!openWrapper) return;
  openWrapper.removeAttribute("data-open");
  openWrapper.querySelector(".explain-toggle")?.setAttribute("aria-expanded", "false");
  openWrapper = undefined;
}

/**
 * Place the bubble under its trigger, in viewport coordinates.
 *
 * `position: fixed`, and that is the substantive choice rather than a detail.
 * `.tensor-view-pane .card` sets `overflow-x: auto`, which the CSS overflow
 * spec turns into `overflow-y: auto` as well, so every card on the page is a
 * scroll container — an absolutely positioned bubble would be clipped by the
 * card it sits in, worst at the bottom edge where there is least room. A fixed
 * element escapes ancestor overflow entirely (no ancestor here establishes a
 * containing block for it), at the cost of having to be positioned by hand and
 * closed on scroll.
 */
function position(wrapper: HTMLElement): void {
  const button = wrapper.querySelector<HTMLElement>(".explain-toggle");
  const note = wrapper.querySelector<HTMLElement>(".explain-note");
  if (!button || !note) return;
  const anchor = button.getBoundingClientRect();
  const bubble = note.getBoundingClientRect();

  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(
      anchor.left + anchor.width / 2 - bubble.width / 2,
      window.innerWidth - bubble.width - VIEWPORT_MARGIN,
    ),
  );
  // Below by default, flipped above when below would run off the bottom — and
  // clamped, so a bubble taller than the space either way is still reachable
  // rather than half off-screen.
  const below = anchor.bottom + BUBBLE_OFFSET;
  const top =
    below + bubble.height > window.innerHeight - VIEWPORT_MARGIN
      ? Math.max(VIEWPORT_MARGIN, anchor.top - bubble.height - BUBBLE_OFFSET)
      : below;

  note.style.left = `${Math.round(left)}px`;
  note.style.top = `${Math.round(top)}px`;
}

function open(wrapper: HTMLElement): void {
  if (closeTimer !== undefined) clearTimeout(closeTimer);
  closeTimer = undefined;
  if (openWrapper === wrapper) {
    position(wrapper);
    return;
  }
  close();
  wrapper.setAttribute("data-open", "true");
  wrapper.querySelector(".explain-toggle")?.setAttribute("aria-expanded", "true");
  openWrapper = wrapper;
  // Measured after the attribute is set and before the browser paints, so the
  // bubble is laid out at its real size but never seen at the wrong place.
  position(wrapper);
}

/**
 * Escape and scroll, bound to the document exactly once.
 *
 * Escape has to be global: 1.4.13's "dismissible" means dismissing *without*
 * moving the pointer or the focus, and a bubble opened by hover has neither on
 * it. Scroll closes rather than re-positions — a fixed bubble left behind by a
 * scrolled page is pointing at nothing, and re-positioning it on every scroll
 * frame buys a tooltip that chases you.
 */
function attachGlobalListeners(): void {
  if (globalListenersAttached) return;
  globalListenersAttached = true;
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && openWrapper) {
      // Not `preventDefault`: Escape may legitimately mean something else to
      // whatever has focus, and this one closed a tooltip that had none.
      close();
    }
  });
  document.addEventListener("scroll", () => close(), { capture: true, passive: true });
  window.addEventListener("resize", () => close());
}

/**
 * Wire every explain icon inside `root`.
 *
 * Called after each render, on freshly created markup — the pane's contents are
 * replaced wholesale — so the per-icon listeners below go on nodes that did not
 * exist a moment ago and cannot accumulate. The document-level listeners are the
 * exception and are attached once.
 */
export function wireExplain(root: ParentNode): void {
  close();
  attachGlobalListeners();

  const wrappers = [...root.querySelectorAll<HTMLElement>(".explain")];
  const buttons = wrappers
    .map((wrapper) => wrapper.querySelector<HTMLElement>(".explain-toggle"))
    .filter((button): button is HTMLElement => button !== null);
  if (buttons.length === 0) return;

  // One tab stop for the whole set. See the module comment for why these are
  // not twenty.
  setRovingTabindex(buttons, 0);

  const focusAt = (index: number): void => {
    const next = buttons[Math.min(Math.max(index, 0), buttons.length - 1)];
    if (!next) return;
    setRovingTabindex(buttons, buttons.indexOf(next));
    next.focus();
  };

  wrappers.forEach((wrapper, index) => {
    const button = wrapper.querySelector<HTMLElement>(".explain-toggle");
    if (!button) return;

    wrapper.addEventListener("mouseenter", () => open(wrapper));
    wrapper.addEventListener("mouseleave", () => {
      if (closeTimer !== undefined) clearTimeout(closeTimer);
      closeTimer = setTimeout(close, HOVER_GRACE_MS);
    });

    button.addEventListener("focus", () => {
      setRovingTabindex(buttons, index);
      open(wrapper);
    });
    button.addEventListener("blur", () => close());

    // Touch has no hover at all, so the tap has to do the work. Toggle rather
    // than open, or a second tap on the same icon would look like a dead
    // control.
    button.addEventListener("click", () => {
      if (openWrapper === wrapper) close();
      else open(wrapper);
    });

    button.addEventListener("keydown", (event) => {
      if (!(event instanceof KeyboardEvent)) return;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          focusAt(index + 1);
          return;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          focusAt(index - 1);
          return;
        case "Home":
          event.preventDefault();
          focusAt(0);
          return;
        case "End":
          event.preventDefault();
          focusAt(buttons.length - 1);
          return;
        default:
      }
    });
  });
}
